import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ConnectAttributes } from '../enums/connect-attributes/connect-attributes.enum';
import { EnvironmentService } from '../environment/environment.service';

export interface ConnectAttribute {
    id?: string;
    name: string;
    objectType: string;
    projectId: string;
    dataType: string;
    createdOn?: string;
    modifiedOn?: string;
    isRequired?: string;
    sequence?: string;
    value?: string;
    objectId?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ConnectAttributeService {
    constructor(private http: HttpClient, private env: EnvironmentService) {}

    /**
     * Get a connect attribute value for a project
     * @param projectId The project ID
     * @param attributeName The name of the attribute to fetch
     * @returns The attribute value (string) or null if not found
     */
    public async getAttribute(projectId: string, attributeName: ConnectAttributes): Promise<ConnectAttribute | null> {
        try {
            // First, get all attributes to find the attribute by name
            const allAttributesUrl = `${this.env.connectApiUrl}/attributes?projectId=${projectId}&objectType=PROJECT`;
            const allAttributes = await lastValueFrom(this.http.get<ConnectAttribute[]>(allAttributesUrl));

            let attribute = allAttributes?.find(attr => attr.name === attributeName);

            // If attribute doesn't exist, create it
            if (!attribute) {
                attribute = await this.createAttribute(projectId, attributeName);
                if (!attribute) {
                    return null;
                }
            }

            // Get the attribute value
            const attributeUrl = `${this.env.connectApiUrl}/projects/${projectId}/attributes/${attribute.id}`;
            const attributeWithValue = await lastValueFrom(this.http.get<ConnectAttribute>(attributeUrl));

            return attributeWithValue || null;
        } catch (error) {
            console.error('Error getting connect attribute:', error);
            return null;
        }
    }

    /**
     * Set a connect attribute value for a project
     * @param projectId The project ID
     * @param attributeName The name of the attribute to set
     * @param value The value to set
     * @returns True if successful, false otherwise
     */
    public async setAttribute(projectId: string, attributeName: ConnectAttributes, value: string): Promise<boolean> {
        try {
            // First, get all attributes to find the attribute by name
            const allAttributesUrl = `${this.env.connectApiUrl}/attributes?projectId=${projectId}&objectType=PROJECT`;
            const allAttributes = await lastValueFrom(this.http.get<ConnectAttribute[]>(allAttributesUrl));

            let attribute = allAttributes?.find(attr => attr.name === attributeName);

            // If attribute doesn't exist, create it
            if (!attribute) {
                attribute = await this.createAttribute(projectId, attributeName);
                if (!attribute) {
                    return false;
                }
            }

            // Set the attribute value
            const attributeUrl = `${this.env.connectApiUrl}/projects/${projectId}/attributes/${attribute.id}`;
            await lastValueFrom(this.http.patch(attributeUrl, { value }));

            return true;
        } catch (error) {
            console.error('Error setting connect attribute:', error);
            return false;
        }
    }

    /**
     * Create a new connect attribute
     * @param projectId The project ID
     * @param attributeName The name of the attribute to create
     * @returns The created attribute or null if failed
     */
    private async createAttribute(
        projectId: string,
        attributeName: ConnectAttributes
    ): Promise<ConnectAttribute | null> {
        try {
            const createUrl = `${this.env.connectApiUrl}/attributes`;
            const newAttribute: ConnectAttribute = {
                name: attributeName,
                objectType: 'PROJECT',
                dataType: 'STRING',
                projectId: projectId
            };

            const createdAttribute = await lastValueFrom(this.http.post<ConnectAttribute>(createUrl, newAttribute));

            return createdAttribute;
        } catch (error) {
            console.error('Error creating connect attribute:', error);
            return null;
        }
    }
}
